import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import {
  WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS,
  WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT,
  WEB_COMPONENT_XDR_LIVE_SEARCH_QUERY_PANEL,
} from '../../../webcomponents.constants';
import { SmartViewInlineActions } from '../../../grid/gridSmartViews/types';
import { GzPushBodyLayoutWebComponent } from '../../../ui/gzPushBodyLayout';
import { XdrLiveSearchQueryPanelWebComponent } from '../../xdrLiveSearchQueryPanel';
import { LIVESEARCH_ITEM_TYPES, LivesearchEvents, SMART_VIEW_MAX_INPUT_LENGTH, LocalState, LivesearchLabelParts } from '../types';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { GenericGridSmartViewsWebComponent } from '../../../grid/genericGridSmartViews';
import { GzLoaderWebComponent } from '../../../ui/gzLoader';
import {
  CategoriesConfig,
  SmartViewItemConfiguration,
  ValidateViewNameErrorCodeResponse,
} from '../../../grid/genericGridSmartViews/types';
import _ from 'lodash';

export class XdrSearchLiveView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $detailsPanel: HTMLElement;
    $pushBodyLayout: GzPushBodyLayoutWebComponent;
    $searchQueryPanel: XdrLiveSearchQueryPanelWebComponent;
    $smartViewsTitle: GzTitleWebComponent;
    $gridSmartViews: GenericGridSmartViewsWebComponent;
    $loader: GzLoaderWebComponent;
  };
  protected activeItem: any;
  public initialStateOfCurrentItem: LocalState;
  public currentStateOfCurrentItem: LocalState;

  getInitialStateOfCurrentItem(): LocalState {
    return this.initialStateOfCurrentItem;
  }

  getCurrentStateOfCurrentItem(): LocalState {
    return this.currentStateOfCurrentItem;
  }

  resetFiltersOfCurrentStateToInitial(): void {
    this.currentStateOfCurrentItem = {
      ...this.currentStateOfCurrentItem,
      targetFilters: this.initialStateOfCurrentItem['targetFilters'],
    };
  }

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$smartViewsTitle = this.shadow.querySelector('#smart-views-title');
    this.dom.$detailsPanel = this.shadow.querySelector('gz-side-menu');
    this.dom.$searchQueryPanel = this.shadow.querySelector(WEB_COMPONENT_XDR_LIVE_SEARCH_QUERY_PANEL);
    this.dom.$searchQueryPanel.updateComplete.then(() => {
      this.dom.$searchQueryPanel.injectDependencies(this.dependencies);
    });
    this.dom.$searchQueryPanel.translateFn = this.dependencies.translateFn;
    this.dom.$pushBodyLayout = this.shadow.querySelector(WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT);
    this.dom.$pushBodyLayout.isOpen = true;
    this.dom.$gridSmartViews = this.shadow.querySelector(WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS);
    this.dom.$loader = this.shadow.querySelector('gz-push-body-layout .loader');
    this.getTranslations();
    this.setTranslatedStrings();
  }

  private async buildSmartViews() {
    if (!(this.dom.$gridSmartViews instanceof GenericGridSmartViewsWebComponent)) {
      throw new Error('Missing grid smart views');
    }

    // construct initial view
    const targetFilters = this.dependencies.getUser().mdrSuperAnalyst ? this.dependencies.getActiveCompany() : this.dependencies.getUserCompany();
    this.dom.$gridSmartViews.categoriesConfig = this.getSmartViewsCategories();
    this.dom.$gridSmartViews.hasSearchInput = true;
    this.dom.$gridSmartViews.initialView = {
      isActiveViewOnLoad: true,
      view: {
        label: this.dependencies.translateFn('New query'),
        specifics: {
          targetFilters: targetFilters,
          queryContent: '',
        },
      },
      category: LIVESEARCH_ITEM_TYPES.FEATURED
    };
    this.dom.$gridSmartViews.isEditedFn = this.isEdited.bind(this);
    this.dom.$gridSmartViews.searchFn = this.filterSmartViews.bind(this);
    this.dom.$gridSmartViews.decoratedTextOnSearchFn = this.decoratedTextOnSearch.bind(this);
    this.dom.$gridSmartViews.emptySavedViewsText = this.dependencies.translateFn('No search queries found');
    this.dom.$gridSmartViews.searchPlaceholderString = this.dependencies.translateFn('Search queries');
    this.dom.$gridSmartViews.customErrorMappingFn = this.getSmartViewNameValidationErrorMessage.bind(this);

    this.dom.$gridSmartViews.injectDependencies(this.dependencies);

    await this.dom.$gridSmartViews.start();
    this.setElementClass(this.dom.$loader, 'hidden');
    this.removeElementClass(this.dom.$gridSmartViews, 'hidden');
  }

  private getSmartViewsCategories(): CategoriesConfig {
    return {
      internalCategories: {
        recent: {
          isAvailable: true,
          translations: {},
          emptyStateItemText: this.dependencies.translateFn('No recent queries added'),
        },
        favorites: {
          isAvailable: false,
          translations: {},
        },
        default: {
          isOpen: false,
          isAvailable: true,
          sectionTitle: this.dependencies.translateFn('Predefined'),
          emptyStateItemText: this.dependencies.translateFn('No predefined queries added'),
          translations: {
            new_query: this.dependencies.translateFn('New query'),
          },
        },
      },
      customCategories: [
        {
          sectionTitle: this.dependencies.translateFn('Saved'),
          emptyStateItemText: this.dependencies.translateFn('No queries added'),
          type: LIVESEARCH_ITEM_TYPES.SAVED,
          inlineActions: [
            {
              title: this.dependencies.translateFn('Rename'),
              value: SmartViewInlineActions.RENAME,
            },
            {
              title: this.dependencies.translateFn('Delete'),
              value: SmartViewInlineActions.DELETE,
              customClass: 'danger',
            },
          ],
          isOpen: false,
          smartViewMaxInputLength: SMART_VIEW_MAX_INPUT_LENGTH,
          noSpaceEscape: true,
          smartViewValidators: [],
          items: [],
        },
        {
          sectionTitle: this.dependencies.translateFn('Featured'),
          emptyStateItemText: this.dependencies.translateFn('No Featured queries added'),
          type: LIVESEARCH_ITEM_TYPES.FEATURED,
          inlineActions: [],
          isOpen: true,
          smartViewMaxInputLength: SMART_VIEW_MAX_INPUT_LENGTH,
          noSpaceEscape: true,
          smartViewValidators: [],
          items: [],
        }
      ],
    };
  }

  public getSmartViewNameValidationErrorMessage(errorCode: number): string | null {
    switch (errorCode) {
      case ValidateViewNameErrorCodeResponse.INVALID_VIEWNAME_LENGTH_CODE:
        return this.dependencies.translateFn('The query name must have a maximum of 64 characters.');

      case ValidateViewNameErrorCodeResponse.INVALID_VIEWNAME_DUPLICATE_NAME_CODE:
        return this.dependencies.translateFn('A query with this name already exists.');

      case ValidateViewNameErrorCodeResponse.INVALID_VIEWNAME_INVALID_CHARACTERS:
        return this.dependencies.translateFn('The query name must not contain special characters.');

      default:
        return null;
    }
  }

  public setActiveViewItem(activeViewItem: any): void {
    this.activeItem = activeViewItem;
    this.initialStateOfCurrentItem = {
      queryContent: activeViewItem.specifics.queryContent === undefined ? '' : activeViewItem.specifics.queryContent,
      targetFilters: activeViewItem.specifics.targetFilters,
    };
    if (activeViewItem.specifics.targetFilters.operatingSystems == null) {
      this.initialStateOfCurrentItem['targetFilters']['operatingSystems'] =
        this.dom.$searchQueryPanel.getAllOptionsValuesOperatingSystems();
    }
    if (activeViewItem.specifics.targetFilters.endpoints == null) {
      const defaultEndpointValue = this.dom.$searchQueryPanel.getSpecialSelectedOptionEndpointFilter();
      if (defaultEndpointValue) {
        this.initialStateOfCurrentItem['targetFilters']['endpoints'] = defaultEndpointValue;
      }
    } else {
      this.initialStateOfCurrentItem['targetFilters']['endpoints'] =
        this.initialStateOfCurrentItem['targetFilters']['endpoints'].sort();
    }
    this.initialStateOfCurrentItem['targetFilters']['tags'] =
      activeViewItem.specifics.targetFilters.tags == null
        ? []
        : this.initialStateOfCurrentItem['targetFilters']['tags'].sort();

    this.currentStateOfCurrentItem = _.cloneDeep(this.initialStateOfCurrentItem);
  }

  public setModifiedStateValue(updatedStateObject: object): void {
    this.currentStateOfCurrentItem = _.mergeWith(
      {},
      this.currentStateOfCurrentItem,
      updatedStateObject,
      (objValue, srcValue): object => (_.isArray(objValue) ? srcValue : undefined)
    );
    this.renderModifiedState();
  }

  public renderModifiedState(): void {
    if (this.isEdited()) {
      this.dom.$searchQueryPanel.addModifyMarkerToTitle();
      return;
    }
    this.dom.$searchQueryPanel.removeModifyMarkerFromTitle();
  }

  public isEdited(): boolean {
    return !_.isEqual(this.initialStateOfCurrentItem, this.currentStateOfCurrentItem);
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getXdrSearchStrings();
    this.translations.inlineActionsTranslations = this.stringsTranslator.getInlineActionsCommonStrings();
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    this.dom.$smartViewsTitle.innerHTML = this.dependencies.translateFn('QUERIES');
  }

  public async buildView(data: any) {
    this.displaySearchQueryPanel();
    await this.loadSmartViewItems();
  }

  public loadSmartViewItems() {
    this.buildSmartViews().then(() => {
      this.dom.$wrapper.dispatchEvent(
        new CustomEvent(LivesearchEvents.SMART_VIEWS_LOADED, {
          detail: {},
          bubbles: true,
          composed: true,
        })
      );
    });
  }

  private displaySearchQueryPanel() {
    const {
      invalidQuery,
      invalidField,
      invalidOperator,
      missingValue,
      invalidValue,
      missingOperator,
      titleLiveQueryPanel,
      queryBtnString,
      clearBtnString,
    } = this.translations;

    const errorMessages = {
      invalid_query: invalidQuery,
      invalid_field: invalidField,
      invalid_operator: invalidOperator,
      missing_value: missingValue,
      invalid_value: invalidValue,
      missing_operator: missingOperator,
    };

    this.dom.$searchQueryPanel.setAttribute('pageTitle', titleLiveQueryPanel);
    this.dom.$searchQueryPanel.searchQueryProps = {
      queryInputPlaceholder: '',
      queryBtnString: queryBtnString,
      clearBtnString: clearBtnString,
      errorMessages: errorMessages,
      hintTextString: '',
      helperHref: '',
      manualResize: true,
      enterKeyEnabled: true,
    };
  }

  /**
   * case-insensitive search for the words in search term to be found in a label or specifics.queryContent
   */
  private filterSmartViews(filter: string, item: SmartViewItemConfiguration): boolean {
    if (filter === '') {
      return true;
    }
    if (typeof item.specifics.queryContent !== 'string') {
      return false;
    }

    filter = filter.toLowerCase();
    const label = this.getItemLabelParts(item).searchableLabelPart;
    const lowercaseLabel = label.trim().toLowerCase();
    const lowercaseSpecifics = item.specifics.queryContent.trim().toLowerCase();
    const filterWords = filter.split(/\s+/);
    for (const filterWord of filterWords) {
      if (!lowercaseLabel.includes(filterWord) && !lowercaseSpecifics.includes(filterWord)) {
        return false;
      }
    }

    return true;
  }

  private decoratedTextOnSearch(filter: string, item: SmartViewItemConfiguration): string {
    if (filter === '') {
      return item.label;
    }
    filter = filter.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&').replace(/\s+/g, '|');

    const labelParts = this.getItemLabelParts(item);
    return (
      labelParts.timePrefix +
      labelParts.draftPrefix +
      labelParts.searchableLabelPart.replace(new RegExp('(' + filter + ')', 'ig'), '<b>$1</b>')
    );
  }

  private getItemLabelParts(item: SmartViewItemConfiguration): LivesearchLabelParts {
    let timePrefix = '';
    let draftPrefix = '';
    let searchableLabelPart = item.label;
    if (item.isRecent) {
      //a recent query can have the format [1 Feb 2023, 10:26][Draft] name query
      // or [1 Feb 2023, 10:26] name query
      let labelParts;
      //put all .*] or rest of the string parts in an array
      const matches = item.label.match(/.*?]|.+$/g);
      if (matches !== null && matches.length >= 2) {
        if (item.specifics.isQueryEdited) {
          //draft is included
          [timePrefix, draftPrefix, ...labelParts] = matches;
        } else {
          [timePrefix, ...labelParts] = matches;
        }
        searchableLabelPart = labelParts.join();
      }
    }

    return {
      timePrefix: timePrefix,
      draftPrefix: draftPrefix,
      searchableLabelPart: searchableLabelPart,
    };
  }
}
